import * as echarts from "echarts";
import type { ChartConfig } from "@bi-studio/shared/lib";
import type { ChartData, ChartSize } from "./types";
import {
    getChartInstance,
    setupResize,
    clearContainer,
    resolveColors,
} from "./utils";

export const renderAreaChart = (
    container: HTMLElement | null,
    data: ChartData,
    config: ChartConfig,
    _size?: ChartSize,
    theme: 'light' | 'dark' = 'light'
): HTMLElement | null => {
    if (!container || typeof window === "undefined") return null;

    clearContainer(container);

    // Set container size
    container.style.width = "100%";
    container.style.height = "100%";

    const chartInstance = getChartInstance(container, theme);
    const colors = resolveColors(config, theme);
    const opacity = typeof config.opacity === 'number' ? config.opacity : 0.4;

    // Determine curve type
    let smooth = false;
    let step: false | "start" | "end" | "middle" = false;
    if (config.curve === "basis") {
        smooth = true;
    } else if (config.curve === "step") {
        step = "start";
    }

    // Common series properties
    const getAreaStyle = (index: number) => ({
        color: colors[index % colors.length],
        opacity: opacity,
    });

    let series: any[] = [];
    let legendData: string[] = [];
    const xValues = [...new Set(data.map((row) => String(row[config.xField ?? ""] ?? "")))];

    // Handle three modes: seriesField, yFields, or single yField
    if (config.seriesField) {
        const grouped: Record<string, Array<{ name: string; value: number }>> = {};
        data.forEach((row) => {
            const seriesKey = String(row[config.seriesField ?? ""] ?? "");
            if (!grouped[seriesKey]) {
                grouped[seriesKey] = [];
            }
            grouped[seriesKey].push({
                name: String(row[config.xField ?? ""] ?? ""),
                value: Number(row[config.yField ?? ""]) || 0,
            });
        });

        legendData = Object.keys(grouped);
        series = legendData.map((seriesKey, index) => {
            const seriesData = grouped[seriesKey];
            const mappedData = xValues.map((x) => {
                const item = seriesData.find((d) => d.name === x);
                return item ? item.value : 0;
            });

            return {
                name: seriesKey,
                type: "line",
                data: mappedData,
                smooth,
                ...(step && { step }),
                ...(config.stacked && { stack: "Total" }),
                areaStyle: getAreaStyle(index),
                lineStyle: { width: 2 },
                symbol: 'none',
                emphasis: {
                    focus: 'series'
                }
            };
        });
    } else if (config.yFields && config.yFields.length > 0) {
        legendData = config.yFields.filter((f) => !!f);
        series = legendData.map((field, index) => ({
            name: field,
            type: "line",
            data: data.map((row) => Number(row[field]) || 0),
            smooth,
            ...(step && { step }),
            ...(config.stacked && { stack: "Total" }),
            areaStyle: getAreaStyle(index),
            lineStyle: { width: 2, color: colors[index % colors.length] },
            itemStyle: { color: colors[index % colors.length] },
            symbol: 'none',
            emphasis: {
                focus: 'series'
            }
        }));
    } else {
        // Single series
        const yValues = data.map((row) => Number(row[config.yField ?? ""]) || 0);
        series = [
            {
                name: config.yField || "Value",
                type: "line",
                data: yValues,
                smooth,
                ...(step && { step }),
                areaStyle: getAreaStyle(0),
                lineStyle: {
                    width: 2,
                    color: colors[0]
                },
                itemStyle: {
                    color: colors[0]
                },
                symbol: 'none',
            },
        ];
    }

    const option: echarts.EChartsOption = {
        backgroundColor: 'transparent',
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: 'cross',
                label: {
                    backgroundColor: '#6a7985'
                }
            }
        },
        legend: legendData.length > 0 ? {
            data: legendData,
            top: 10,
        } : undefined,
        grid: {
            left: "3%",
            right: "4%",
            bottom: "10%",
            top: legendData.length > 0 ? "15%" : "10%",
            containLabel: true,
        },
        xAxis: {
            type: "category",
            boundaryGap: false,
            data: xValues,
            axisLabel: {
                rotate: 45,
            },
        },
        yAxis: {
            type: "value",
        },
        series,
    };

    chartInstance.setOption(option);
    setupResize(container, chartInstance);

    return container;
};
